import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Eye, AlertTriangle, Minus, Plus } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const PdfPreview = forwardRef(function PdfPreview({ number, lang = "en", values, enabled = true, interactive = false }, ref) {
  const [url, setUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [width, setWidth] = useState(560);
  const [zoom, setZoom] = useState(1);
  const wrapRef = useRef(null);
  const urlRef = useRef(null);
  const pdfDocRef = useRef(null);
  const valuesKey = JSON.stringify(values);

  useEffect(() => {
    const measure = () => { if (wrapRef.current) setWidth(wrapRef.current.clientWidth - 24); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const generate = useCallback(async () => {
    setBusy(true);
    setError(false);
    try {
      const res = await api.post(`/forms/${number}/pdf?lang=${lang}`, { field_values: values }, { responseType: "blob" });
      const objUrl = URL.createObjectURL(res.data);
      const prev = urlRef.current;
      urlRef.current = objUrl;
      setUrl(objUrl);
      if (prev) setTimeout(() => URL.revokeObjectURL(prev), 1000);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number, lang, valuesKey]);

  // Read-only mode: auto live-update as fields change (debounced).
  useEffect(() => {
    if (!enabled || interactive) return;
    const t = setTimeout(generate, 900);
    return () => clearTimeout(t);
  }, [enabled, interactive, generate]);

  // Interactive mode: render once from current values (do not auto-refresh, which would wipe edits).
  useEffect(() => {
    if (!interactive) return;
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, number, lang]);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  // Expose the values the user typed directly onto the PDF (read from the
  // PDF.js form annotation DOM — each input's name is the AcroForm field name).
  useImperativeHandle(ref, () => ({
    getEditedValues: async () => {
      const root = wrapRef.current;
      if (!root) return {};
      const out = {};
      root.querySelectorAll(".annotationLayer input, .annotationLayer textarea, .annotationLayer select").forEach((el) => {
        const name = el.name || el.getAttribute("name");
        if (!name) return;
        if (el.type === "checkbox" || el.type === "radio") {
          if (el.checked) out[name] = true;
        } else if (el.value !== undefined && el.value !== "") {
          out[name] = el.value;
        }
      });
      return out;
    },
    refresh: generate,
  }), [generate]);

  const file = useMemo(() => (url ? { url } : null), [url]);
  const showRefreshBtn = !enabled && !interactive;
  const pageWidth = Math.max(200, Math.round(width * zoom));
  const zoomOut = () => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)));
  const zoomIn = () => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)));

  return (
    <div ref={wrapRef} data-testid="original-pdf-view"
      className="relative w-full h-[80vh] overflow-auto border border-border rounded-sm bg-slate-200 dark:bg-slate-800">
      {/* Zoom controls */}
      <div className="sticky top-0 z-20 flex items-center justify-between px-2 py-1.5 bg-background/95 backdrop-blur border-b border-border">
        <div className="inline-flex items-center gap-1 border-2 border-foreground rounded-sm bg-background text-foreground" data-testid="pdf-zoom-controls">
          <button type="button" onClick={zoomOut} disabled={zoom <= 0.5} data-testid="pdf-zoom-out"
            title="Decrease size" className="h-7 w-7 grid place-items-center hover:bg-secondary disabled:opacity-40">
            <Minus size={15} />
          </button>
          <span className="text-xs font-semibold w-11 text-center tabular-nums text-foreground" data-testid="pdf-zoom-level">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={zoomIn} disabled={zoom >= 2.5} data-testid="pdf-zoom-in"
            title="Enlarge size" className="h-7 w-7 grid place-items-center border-l-2 border-foreground hover:bg-secondary disabled:opacity-40">
            <Plus size={15} />
          </button>
        </div>
        {busy && (
          <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <Loader2 size={12} className="animate-spin" /> Updating…
          </span>
        )}
        {showRefreshBtn && !busy && (
          <Button size="sm" variant="secondary" onClick={generate} data-testid="preview-refresh-btn"
            className="rounded-sm gap-1 h-7 text-xs">
            <RefreshCw size={12} /> {url ? "Refresh" : "Generate"} preview
          </Button>
        )}
      </div>

      <div className="py-3 px-3" style={{ minWidth: "min-content" }}>
      {file ? (
        <div style={{ width: pageWidth }} className={`mx-auto ${interactive ? "" : "pointer-events-none select-none"}`}>
          <Document file={file}
            onLoadSuccess={(pdf) => { pdfDocRef.current = pdf; setNumPages(pdf.numPages); }}
            loading={<div className="p-8 text-sm text-slate-500">Rendering document…</div>}
            error={<div className="p-8 text-sm text-slate-500">Could not render preview.</div>}>
            {Array.from({ length: numPages }, (_, i) => (
              <Page key={i} pageNumber={i + 1} width={pageWidth} renderForms renderTextLayer={false}
                className="mb-3 shadow-md bg-white" />
            ))}
          </Document>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 text-sm text-slate-600 p-10 text-center" data-testid="preview-error">
          <AlertTriangle size={28} className="text-amber-500" />
          Couldn’t build the preview. Please try again.
          <Button size="sm" variant="secondary" onClick={generate} disabled={busy} data-testid="preview-retry-btn"
            className="rounded-sm gap-1 h-8">
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Retry
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-sm text-slate-500 p-10 text-center">
          <Eye size={28} className="text-slate-400" />
          {busy || enabled || interactive ? "Preparing preview…" : "Live preview is off. Click “Generate preview” to render the filled form."}
        </div>
      )}
      </div>
    </div>
  );
});

export default PdfPreview;
